import type { FC } from 'react';
import { useId } from 'react';
import { Box } from '@mui/material';

import type { DashboardWidgetProps } from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import ComparisonTableChart from 'src/components/dashboard/charts/ComparisonTableChart';
import { useExportOptions } from 'src/components/dashboard/charts/ComparisonTableChart/hooks';
import WidgetFrame from 'src/components/dashboard/DashboardPage/components/WidgetFrame';
import { useComparisonExportData } from 'src/components/dashboard/hooks/widgetData/useComparisonExportData';
import type { StackedNumbersList } from 'src/components/followUp/common/types';
import NoResultsToShowAdjustFiltersWarning from 'src/components/followUp/common/warning/NoResultsToShowAdjustFiltersWarning';
import WidgetHeaderWithPill from 'src/components/followUp/common/WidgetHeaderWithPill/WidgetHeaderWithPill';
import useGenerateWidgetEndpoint from 'src/components/followUp/hooks/useGenerateWidgetEndpoint';
import useWidgetQuery from 'src/components/followUp/hooks/useWidgetQuery';

/*
This widget has essentially become two widgets. The one shown on EOS is completely different from the one shown in HRQS.
The next time we need to make changes to this widget that further separate the two use cases, we should split it into
two separate widgets.
 */
const ComparisonOfUnitsWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const ariaId = useId();
  const endpoint = useGenerateWidgetEndpoint(
    apiPaths.WIDGET_DATA.COMPARISON_OF_UNITS,
    config.id,
  );
  const { data, isFetching, errorMsg, refetch, comparisonData, queryFilters } =
    useWidgetQuery<StackedNumbersList>(endpoint);
  const comparisonExportData = useComparisonExportData(data);
  const exportOptions = useExportOptions({
    data: comparisonExportData,
    widgetName: config.name,
    queryFilters,
  });

  return (
    <WidgetFrame
      ariaId={ariaId}
      error={errorMsg}
      loading={isFetching || !data || !comparisonExportData}
      refetch={refetch}
      exportOptions={exportOptions}
      notAffectedBy={comparisonData && 'comparison'}
      header={<WidgetHeaderWithPill title={config.name} ariaId={ariaId} />}
      footer={
        data && data.results.length === 0 ? (
          <NoResultsToShowAdjustFiltersWarning />
        ) : undefined
      }
    >
      <Box sx={{ width: '100%', overflow: 'scroll', height: '100%' }}>
        {!!data && <ComparisonTableChart data={data} />}
      </Box>
    </WidgetFrame>
  );
};
export default ComparisonOfUnitsWidget;
